<template>
  <section class="management-section" data-testid="management-portfolio-comparison">
    <ManagementSectionHeader
      :title="$t('UserProjects.management.comparison.title')"
      :subtitle="$t('UserProjects.management.comparison.subtitle')"
    />

    <div
      v-if="!comparison || comparison.insufficientData"
      class="text-sm text-[var(--color-disabled)] py-3"
      data-testid="management-comparison-insufficient"
    >
      {{ $t('UserProjects.management.comparison.insufficient') }}
    </div>

    <template v-else>
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div
          v-for="tile in averageTiles"
          :key="tile.id"
          class="management-panel-card p-4"
          :data-testid="`management-comparison-avg-${tile.id}`"
        >
          <p class="text-[10px] uppercase tracking-wider text-[var(--color-disabled)] m-0 mb-1">
            {{ tile.label }}
          </p>
          <p class="text-[22px] font-bold text-[var(--color-dark)] m-0 leading-tight">
            {{ tile.value }}
          </p>
        </div>
      </div>

      <div class="management-panel-card overflow-hidden mb-4" data-testid="management-comparison-projects">
        <div
          v-for="(row, index) in visibleProjects"
          :key="row.projectId"
          class="flex flex-wrap items-center gap-3 px-4 py-3"
          :class="{ 'border-t border-[var(--color-light-grey)]': index > 0 }"
          :data-testid="`management-comparison-row-${row.projectId}`"
        >
          <button
            type="button"
            class="text-left flex-1 min-w-[160px] bg-transparent border-0 p-0 cursor-pointer"
            @click="$emit('select-project', row.projectId)"
          >
            <p class="text-[13px] font-semibold text-[var(--color-dark)] m-0">{{ row.projectName }}</p>
            <p class="text-[11px] text-[var(--color-disabled)] m-0">{{ row.riskLevel }}</p>
          </button>
          <div class="text-center shrink-0 w-16">
            <p class="text-[15px] font-bold text-[var(--color-dark)] m-0">
              {{ formatPercent(row.ppcPercent) }}
            </p>
            <p class="text-[10px] text-[var(--color-disabled)] m-0 uppercase">PPC</p>
          </div>
          <div class="text-center shrink-0 w-20">
            <p
              class="text-[13px] font-bold m-0"
              :style="{ color: deltaColor(row.ppcVsAverage) }"
            >
              {{ formatDelta(row.ppcVsAverage) }}
            </p>
            <p class="text-[10px] text-[var(--color-disabled)] m-0">
              {{ $t('UserProjects.management.comparison.vsAvg') }}
            </p>
          </div>
          <div class="text-center shrink-0 w-14">
            <p class="text-[13px] font-bold text-[var(--color-dark)] m-0">
              {{ row.rankByPpc ?? '—' }}
            </p>
            <p class="text-[10px] text-[var(--color-disabled)] m-0">
              {{ $t('UserProjects.management.comparison.rank') }}
            </p>
          </div>
          <div class="text-center shrink-0 w-16">
            <p class="text-[13px] font-bold text-[var(--color-dark)] m-0">
              {{ row.activeConstraintsCount }}
            </p>
            <p class="text-[10px] text-[var(--color-disabled)] m-0 uppercase">C</p>
          </div>
          <div class="text-center shrink-0 w-16">
            <p class="text-[13px] font-bold text-[var(--color-dark)] m-0">
              {{ row.openDecisionsCount }}
            </p>
            <p class="text-[10px] text-[var(--color-disabled)] m-0 uppercase">D</p>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div class="management-panel-card p-4" data-testid="management-comparison-outliers">
          <p class="text-xs font-medium text-[var(--color-dark)] mb-3 m-0">
            {{ $t('UserProjects.management.comparison.outliersTitle') }}
          </p>
          <ul class="list-none m-0 p-0 space-y-2">
            <li
              v-for="item in outlierItems"
              :key="item.id"
              class="text-[12px] text-[var(--color-dark)]"
            >
              <span class="text-[var(--color-disabled)]">{{ item.label }}:</span>
              <button
                v-if="item.projectId"
                type="button"
                class="ml-1 font-medium bg-transparent border-0 p-0 cursor-pointer text-[var(--color-dark)] underline-offset-2 hover:underline"
                @click="$emit('select-project', item.projectId)"
              >
                {{ item.value }}
              </button>
              <span v-else class="ml-1">{{ item.value }}</span>
            </li>
          </ul>
        </div>

        <div class="management-panel-card p-4" data-testid="management-comparison-patterns">
          <p class="text-xs font-medium text-[var(--color-dark)] mb-3 m-0">
            {{ $t('UserProjects.management.comparison.patternsTitle') }}
          </p>
          <div
            v-if="!sharedPatterns.length"
            class="text-sm text-[var(--color-disabled)]"
          >
            {{ $t('UserProjects.management.comparison.patternsEmpty') }}
          </div>
          <div v-else class="space-y-2">
            <div
              v-for="pattern in sharedPatterns"
              :key="pattern.label"
              class="flex items-center gap-3"
            >
              <span class="text-[12px] text-[var(--color-dark)] flex-1 min-w-0 truncate">
                {{ pattern.label }}
              </span>
              <span class="text-[12px] font-bold text-[var(--color-dark)]">{{ pattern.count }}</span>
              <span class="text-[11px] text-[var(--color-disabled)] shrink-0">
                {{ pattern.projectCount }}
                {{ $t('UserProjects.management.comparison.projectsSuffix') }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import ManagementSectionHeader from '@/components/CustomerApp/UserProjects/ManagementSectionHeader.vue';

export default {
  name: 'ManagementPortfolioComparison',
  components: { ManagementSectionHeader },
  props: {
    comparison: {
      type: Object,
      default: null,
    },
  },
  emits: ['select-project'],
  computed: {
    averages() {
      return this.comparison?.averages || {};
    },
    averageTiles() {
      return [
        {
          id: 'ppc',
          label: this.$t('UserProjects.management.comparison.avgPpc'),
          value: this.formatPercent(this.averages.ppcPercent),
        },
        {
          id: 'constraints',
          label: this.$t('UserProjects.management.comparison.avgConstraints'),
          value: this.formatNumber(this.averages.activeConstraints),
        },
        {
          id: 'decisions',
          label: this.$t('UserProjects.management.comparison.avgDecisions'),
          value: this.formatNumber(this.averages.openDecisions),
        },
      ];
    },
    visibleProjects() {
      return (this.comparison?.projects || []).slice(0, 8);
    },
    sharedPatterns() {
      return this.comparison?.sharedPatterns || [];
    },
    outlierItems() {
      const outliers = this.comparison?.outliers || {};
      return [
        {
          id: 'lowestPpc',
          label: this.$t('UserProjects.management.comparison.outlierLowestPpc'),
          projectId: outliers.lowestPpc?.projectId,
          value: outliers.lowestPpc
            ? `${outliers.lowestPpc.projectName} (${outliers.lowestPpc.value}%)`
            : '—',
        },
        {
          id: 'constraints',
          label: this.$t('UserProjects.management.comparison.outlierConstraints'),
          projectId: outliers.highestConstraints?.projectId,
          value: outliers.highestConstraints
            ? `${outliers.highestConstraints.projectName} (${outliers.highestConstraints.value})`
            : '—',
        },
        {
          id: 'decisions',
          label: this.$t('UserProjects.management.comparison.outlierDecisions'),
          projectId: outliers.highestOpenDecisions?.projectId,
          value: outliers.highestOpenDecisions
            ? `${outliers.highestOpenDecisions.projectName} (${outliers.highestOpenDecisions.value})`
            : '—',
        },
      ];
    },
  },
  methods: {
    formatPercent(value) {
      return typeof value === 'number' ? `${value}%` : '—';
    },
    formatNumber(value) {
      return typeof value === 'number' ? String(value) : '—';
    },
    formatDelta(value) {
      if (typeof value !== 'number') return '—';
      const sign = value > 0 ? '+' : '';
      return `${sign}${value} pp`;
    },
    deltaColor(value) {
      if (typeof value !== 'number') return 'var(--color-disabled)';
      if (value < 0) return '#EF4444';
      if (value > 0) return '#22C55E';
      return 'var(--color-dark)';
    },
  },
};
</script>

<style scoped>
@import './management-section.css';
</style>
